import React, { useState } from 'react';
import styled from '@emotion/native';
import Dropdown from '@/shared/components/Dropdown';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import { useShallow } from 'zustand/react/shallow';
import { REASON_OPTIONS } from '@/components/bookConsultation/shared/constants';
import {
  InstantEventType,
  PricingTier,
  QueryResult,
} from '@/shared/types/generated';
import {
  FieldLabel,
  OptionFooterText,
  SubheaderText,
  Title,
} from '../styledComponents';
import {
  DURATION_OPTIONS,
  PRICING_TIER_OPTIONS,
} from '@/shared/constants/instantCall';
import { getDropdownOptionLabel } from '@/shared/utils';
import { useQuery } from '@apollo/client';
import { GET_PRICING } from '@/shared/queries';
import SentryService from '@/services/sentry/Sentry.service';
import { buildPricingTierOptions } from '../helpers';
import useStore from '@/stores/useStore';

const ClientInstantCallPreferences = () => {
  const scrollViewRef = useStore((state) => state.scrollRef);
  const { requestForm, setRequestForm } = useInstantStylingStore(
    useShallow((state) => ({
      requestForm: state.requestForm,
      setRequestForm: state.setRequestForm,
    })),
  );

  const [isDurationMenuVisible, setIsDurationMenuVisible] = useState(false);
  const [isPriceTierMenuVisible, setIsPriceTierMenuVisible] = useState(false);
  const [isReasonMenuVisible, setIsReasonMenuVisible] = useState(false);

  const { data: pricingData } = useQuery<QueryResult<'pricing'>>(GET_PRICING, {
    onError: (error) => {
      SentryService.captureError('Error fetching pricing data', error);
    },
  });

  const dynamicPricingOptions = pricingData
    ? buildPricingTierOptions(pricingData.pricing)
    : PRICING_TIER_OPTIONS;

  return (
    <Container>
      <Title>{`let's find\nyour stylist`}</Title>
      <SubheaderText>
        Set your session details and get ready to connect with the perfect
        match.
      </SubheaderText>
      <FieldLabel>Select the duration:</FieldLabel>
      <Dropdown
        eyebrowText="DURATION"
        eyebrowTextStyle={{ fontSize: 9 }}
        options={DURATION_OPTIONS}
        selectedOption={getDropdownOptionLabel(
          requestForm.duration,
          DURATION_OPTIONS,
        )}
        onSelect={(option) =>
          setRequestForm({
            duration: option.value.toString() as InstantEventType,
          })
        }
        isVisible={isDurationMenuVisible}
        setIsVisible={setIsDurationMenuVisible}
      />

      <FieldLabel>Preferred Price Tier:</FieldLabel>
      <Dropdown
        eyebrowText="PRICE TIER"
        eyebrowTextStyle={{ fontSize: 9 }}
        options={dynamicPricingOptions}
        selectedOption={getDropdownOptionLabel(
          requestForm.pricingTier.toString(),
          dynamicPricingOptions,
        )}
        onSelect={(option) =>
          setRequestForm({
            pricingTier: option.value.toString() as PricingTier,
          })
        }
        isVisible={isPriceTierMenuVisible}
        setIsVisible={setIsPriceTierMenuVisible}
        customDropdownButtonStyle={{ marginBottom: 8 }}
      />
      <OptionFooterText>
        Payment will be processed after your session.
      </OptionFooterText>

      <FieldLabel>Tell Us What You Need:</FieldLabel>
      <Dropdown
        eyebrowText="REASON"
        eyebrowTextStyle={{ fontSize: 9 }}
        options={REASON_OPTIONS}
        selectedOption={getDropdownOptionLabel(
          requestForm.reason,
          REASON_OPTIONS,
        )}
        onSelect={(option) =>
          setRequestForm({ reason: option.value.toString() })
        }
        isVisible={isReasonMenuVisible}
        setIsVisible={(visible) => {
          // Adjust the scroll so entire menu is visible
          if (visible) {
            scrollViewRef?.current?.scrollToEnd({ animated: false });
          }
          setIsReasonMenuVisible(visible);
        }}
      />
    </Container>
  );
};

export default ClientInstantCallPreferences;

const Container = styled.View({
  width: '100%',
  paddingHorizontal: 30,
  paddingBottom: 256,
});
